"use client";

import { useState } from "react";
import Link from "@/components/navigation-link";
import { usePathname } from "next/navigation";
import {
  LayoutDashboard,
  BriefcaseBusiness,
  History,
  Star,
  SlidersHorizontal,
  ChartNoAxesCombined,
  GitCompareArrows,
  Activity,
  Shield,
  Wallet,
  Menu,
  X,
} from "lucide-react";
import clsx from "clsx";
import { SCREENER_TABS } from "./screener/tabs";

const NAV = [
  { href: "/", label: "Dashboard", icon: LayoutDashboard },
  {
    href: "/actual-portfolio",
    label: "Actual portfolio",
    icon: BriefcaseBusiness,
  },
  { href: "/trades", label: "Trade History", icon: History },
  { href: "/watchlist", label: "Watchlist", icon: Star },
  { href: "/screener/pullback", label: "Screener", icon: SlidersHorizontal },
  { href: "/sector", label: "Sector / RRG", icon: ChartNoAxesCombined },
  { href: "/dual-rrg", label: "Dual RRG", icon: GitCompareArrows },
  { href: "/market-breadth", label: "Market Breadth", icon: Activity },
  { href: "/hedge-engine", label: "Hedge Engine", icon: Shield },
];

export default function Sidebar() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <aside className="flex w-full shrink-0 flex-col border-b border-border bg-surface/60 px-4 py-4 sm:w-60 sm:border-b-0 sm:border-r sm:py-6">
      <div className="flex items-center justify-between px-2 sm:mb-8">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/20 text-accent">
            <Wallet size={18} />
          </div>
          <span className="font-semibold tracking-tight">Webull Local</span>
        </div>
        <button
          type="button"
          className="rounded-lg p-2 text-muted sm:hidden"
          aria-label="เมนูหลัก"
          aria-expanded={mobileOpen}
          aria-controls="main-navigation"
          onClick={() => setMobileOpen((open) => !open)}
        >
          {mobileOpen ? <X size={19} /> : <Menu size={19} />}
        </button>
      </div>
      <nav
        id="main-navigation"
        aria-label="Main navigation"
        className={clsx(
          "mt-4 flex-col gap-1 sm:mt-0 sm:flex",
          mobileOpen ? "flex" : "hidden",
        )}
      >
        {NAV.map(({ href, label, icon: Icon }) => {
          const screener = label === "Screener";
          const active = screener ? pathname.startsWith("/screener") : pathname === href;
          return (
            <div key={href}>
              <Link
                href={href}
                prefetch={false}
                onClick={() => setMobileOpen(false)}
                aria-current={active && !screener ? "page" : undefined}
                className={clsx(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  active
                    ? "bg-accent/15 text-foreground"
                    : "text-muted hover:bg-white/5 hover:text-foreground",
                )}
              >
                <Icon
                  size={16}
                  className={active ? "text-accent" : undefined}
                />
                {label}
              </Link>
              {screener && (
                <ul
                  aria-label="Screener strategies"
                  className="ml-5 mt-2 space-y-1 border-l border-border pl-3"
                >
                  {SCREENER_TABS.map((tab) => (
                    <li key={tab.id}>
                      <Link
                        href={tab.href}
                        prefetch={false}
                        onClick={() => setMobileOpen(false)}
                        aria-current={
                          pathname === tab.href ? "page" : undefined
                        }
                        className={clsx(
                          "block rounded-lg px-3 py-2 text-xs transition-colors",
                          pathname === tab.href
                            ? "bg-accent/10 font-medium text-accent"
                            : "text-muted hover:bg-white/5 hover:text-foreground",
                        )}
                      >
                        {tab.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </nav>
      <div className="mt-auto hidden px-2 pt-6 text-xs text-muted sm:block">
        Webull portfolio & screeners
      </div>
    </aside>
  );
}
